import React, { useState, useEffect } from 'react';
import PointsInventoryAPI from '../points/PointsInventoryAPI.js';
import imageService from '../images/imageService.js';
import './InventoryManager.css';

const InventoryManager = () => {
  const userRole = (localStorage.getItem('role') || '').toUpperCase(); // ✅ normalized from localStorage

  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    title: '',
    description: '',
    price: '',
    picture: '',
    startDate: '',
    endDate: '',
  });
  const [editId, setEditId] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [popupMessage, setPopupMessage] = useState('');

  const api = new PointsInventoryAPI();

  useEffect(() => {
    if (userRole === 'MANAGER' || userRole === 'ADMIN') {
      fetchItems();
    }
  }, [userRole]);

  const fetchItems = async () => {
    try {
      const data = await api.getAll();
      setItems(data);
    } catch (error) {
      console.error('Fetch error:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    setImageFile(e.target.files[0]);
  };

  const uploadImage = async () => {
    if (!imageFile) return form.picture || '';
    try {
      const filename = imageFile.name;
      const response = await imageService.uploadImage(imageFile, filename, 'pointinventory');
      return response; // backend should return filename
    } catch (error) {
      console.error('Image upload failed', error);
      return '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const uploadedFilename = await uploadImage();

      const itemData = {
        ...form,
        price: parseInt(form.price, 10),
        picture: uploadedFilename,
      };

      if (editId) {
        await api.updateItem(editId, itemData);
        showPopup('Item updated successfully!');
      } else {
        await api.addItem(itemData);
        showPopup('Item created successfully!');
      }

      setForm({
        title: '',
        description: '',
        price: '',
        picture: '',
        startDate: '',
        endDate: '',
      });
      setImageFile(null);
      setEditId(null);
      fetchItems();
    } catch (error) {
      console.error('Submit error:', error);
      showPopup('Error saving item.');
    }
  };

  const handleEdit = (item) => {
    setForm({
      title: item.title,
      description: item.description,
      price: item.price,
      picture: item.picture,
      startDate: item.startDate,
      endDate: item.endDate,
    });
    setEditId(item.inventory_id);
    window.scrollTo(0, 0);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await api.deleteItem(id);
        fetchItems();
        showPopup('Item deleted successfully!');
      } catch (error) {
        console.error('Delete error:', error);
        showPopup('Error deleting item.');
      }
    }
  };

  const showPopup = (message) => {
    setPopupMessage(message);
    setTimeout(() => setPopupMessage(''), 3000);
  };

  // ✅ Role protection
  if (userRole !== 'MANAGER' && userRole !== 'ADMIN') {
    return <div className="inventory-access-denied">Access Denied</div>;
  }

  return (
    <div className="inventory-manager">
      {popupMessage && <div className="popup-message">{popupMessage}</div>}

      <h1>{editId ? 'Edit' : 'Create'} Inventory Item</h1>
      <form className="inventory-form" onSubmit={handleSubmit}>
        <input type="text" name="title" value={form.title} onChange={handleChange} placeholder="Title" required />
        <textarea name="description" value={form.description} onChange={handleChange} placeholder="Description" required />
        <input type="number" name="price" value={form.price} onChange={handleChange} placeholder="Price (Points)" required min="0" />
        <input type="date" name="startDate" value={form.startDate} onChange={handleChange} required />
        <input type="date" name="endDate" value={form.endDate} onChange={handleChange} required />
        <input type="file" onChange={handleImageChange} accept="image/*" />
        <button type="submit">{editId ? 'Update Item' : 'Create Item'}</button>
      </form>

      <h2>Current Inventory</h2>
      <div className="inventory-list">
        {items.map((item) => (
          <div key={item.inventory_id} className="inventory-card">
            <img src={imageService.getImageUrl('pointinventory', item.picture)} alt={item.title} />
            <h3>{item.title}</h3>
            <p>{item.description}</p>
            <p><strong>Points:</strong> {item.price}</p>
            <p><strong>Start:</strong> {item.startDate}</p>
            <p><strong>End:</strong> {item.endDate}</p>
            <div className="inventory-actions">
              <button onClick={() => handleEdit(item)}>Edit</button>
              <button onClick={() => handleDelete(item.inventory_id)}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default InventoryManager;
